// The office's minisites for one customer — what each COLLEAGUE sent this person, and what the
// customer opened there (`POST /contacts/office-minisites`, the server's
// crm/contactsOfficeMinisites.service.ts). Read only.
//
// One request per customer and viewer, cached in the session cache, so the matches tab's pill and
// the panel it opens (and the CRM side panel) ask once. A realtor in no office never asks: the org
// directory — persisted, and already loaded by every screen that shows a colleague — says so.
import { useRef } from "react";
import useAuth from "hooks/useAuth";
import useOrgDirectory from "hooks/useOrgDirectory";
import useWebotServer from "hooks/useWebotServer";
import { fetchListingsByIds } from "utils/listings/fetchListings";
import type { FirestoreListingHit } from "utils/listings/fetchListings";
import { useSessionCacheValue } from "../../data/sessionCache";
import type { REST_schema } from "../../../../../../@types/sharedSchema";

/** Per viewer as well as per customer: a sign-in as somebody else must never inherit the answer. */
export const officeMinisitesCacheKey = (viewerEmail: string, contactId: string): string =>
  `matches:${contactId}:office-minisites:${viewerEmail}`;

const OFF_KEY = "matches:office-minisites:off";
const OFF_ANSWER: REST_schema.CrmOfficeMinisitesResponse = { enabled: false, minisites: [] };

export type OfficeMinisitesState = {
  /** `off`: no office, or no customer — render nothing. */
  status: "off" | "loading" | "ready" | "error";
  minisites: REST_schema.CrmOfficeMinisite[];
  refresh: () => void;
};

export function useOfficeMinisites(contactId: string | null | undefined): OfficeMinisitesState {
  const { user } = useAuth();
  const { orgs } = useOrgDirectory();
  const { contactsOfficeMinisites } = useWebotServer();
  // The client's functions are not referentially stable; the cache's fetcher reads the latest.
  const fetchRef = useRef(contactsOfficeMinisites);
  fetchRef.current = contactsOfficeMinisites;

  const viewer = (user?.email ?? "").trim().toLowerCase();
  const enabled = Boolean(viewer && contactId && orgs.length > 0);
  const key = enabled ? officeMinisitesCacheKey(viewer, contactId as string) : OFF_KEY;
  const { data, loading, error, refresh } = useSessionCacheValue<REST_schema.CrmOfficeMinisitesResponse>(
    key,
    () => (enabled ? fetchRef.current({ contactId: contactId as string }) : Promise.resolve(OFF_ANSWER)),
    { maxAgeMs: 60_000 }
  );

  if (!enabled) return { status: "off", minisites: [], refresh };
  if (data) return { status: data.enabled ? "ready" : "off", minisites: data.minisites, refresh };
  if (error && !loading) return { status: "error", minisites: [], refresh };
  return { status: "loading", minisites: [], refresh };
}

/**
 * The listings of one colleague's page, read the way the viewer's own page reads its listings
 * (`fetchListingsByIds`: the public listings collection, cached per id, a missing one dropped).
 */
export function useOfficeMinisiteListings(
  minisite: REST_schema.CrmOfficeMinisite,
  enabled: boolean
): { listings: Map<string, FirestoreListingHit>; loading: boolean } {
  const ids = minisite.listings.map((listing) => listing.listingId);
  const key = enabled
    ? `matches:office-minisite-listings:${minisite.minisiteId}:${ids.join("|")}`
    : "matches:office-minisite-listings:off";
  const { data, loading } = useSessionCacheValue<FirestoreListingHit[]>(
    key,
    () => (enabled ? fetchListingsByIds(ids) : Promise.resolve([])),
    { maxAgeMs: 5 * 60_000 }
  );
  return {
    listings: new Map((data ?? []).map((hit) => [hit.objectID, hit])),
    loading: enabled && loading
  };
}
